import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, MapPin, Users, Calendar, X as XIcon, Video, Heart, Sparkles } from 'lucide-react';import { useNavigate } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useMosque } from '../contexts/MosqueContext';
import { getIcon } from '../lib/icons';
import FinancialWidget from '../components/FinancialWidget';
import ImageSlider from '../components/ImageSlider';
import DownloadAppWidget from '../components/DownloadAppWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const rp = (a) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(a || 0);

const HomePage = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { to, mosqueId } = useMosque();
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [infaqPrograms, setInfaqPrograms] = useState([]);
  const carouselRef = useRef(null);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    fetchPrayerTimes();
  }, [settings.location_city]);

  useEffect(() => {
    if (mosqueId) {
      axios.get(`${API_URL}/api/events`, { params: { mosque_id: mosqueId } })
        .then(r => setEvents((r.data || []).slice(0, 6)))
        .catch(() => {});
      axios.get(`${API_URL}/api/infaq`, { params: { mosque_id: mosqueId } })
        .then(r => setInfaqPrograms(r.data || []))
        .catch(() => {});
    }
  }, [mosqueId]);

  // Carousel scroll tracking
  const handleCarouselScroll = useCallback(() => {
    const el = carouselRef.current;
    if (!el || !infaqPrograms.length) return;
    const cardWidth = el.scrollWidth / infaqPrograms.length;
    const idx = Math.min(Math.round(el.scrollLeft / cardWidth), infaqPrograms.length - 1);
    setCarouselIdx(idx);
  }, [infaqPrograms.length]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleCarouselScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleCarouselScroll);
  }, [handleCarouselScroll]);

  useEffect(() => {
    if (prayerTimes) {
      const interval = setInterval(() => {
        updateCountdown();
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [prayerTimes]);

  const fetchPrayerTimes = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/prayer-times`, {
        params: {
          city: settings.location_city,
          country: 'Indonesia'
        }
      });
      setPrayerTimes(data.data.timings);
    } catch (error) {
      console.error('Error fetching prayer times:', error);
      // Fallback prayer times untuk Jakarta
      setPrayerTimes({
        Fajr: '04:45',
        Dhuhr: '12:00',
        Asr: '15:15',
        Maghrib: '18:05',
        Isha: '19:15'
      });
    }
  };

  const updateCountdown = () => {
    if (!prayerTimes) return;

    const now = new Date();
    const prayers = [
      { name: 'Subuh', time: prayerTimes.Fajr },
      { name: 'Dzuhur', time: prayerTimes.Dhuhr },
      { name: 'Ashar', time: prayerTimes.Asr },
      { name: 'Maghrib', time: prayerTimes.Maghrib },
      { name: 'Isya', time: prayerTimes.Isha }
    ];

    let next = null;
    for (const prayer of prayers) {
      const [hours, minutes] = prayer.time.split(':');
      const prayerDate = new Date();
      prayerDate.setHours(parseInt(hours), parseInt(minutes), 0);

      if (prayerDate > now) {
        next = { ...prayer, date: prayerDate };
        break;
      }
    }

    if (!next) {
      const [hours, minutes] = prayers[0].time.split(':');
      const prayerDate = new Date();
      prayerDate.setDate(prayerDate.getDate() + 1);
      prayerDate.setHours(parseInt(hours), parseInt(minutes), 0);
      next = { ...prayers[0], date: prayerDate };
    }

    setNextPrayer(next);

    const diff = next.date - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    setCountdown(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
  };

  const getGreeting = () => {
    if (settings.greeting_text && settings.greeting_text.trim()) return settings.greeting_text;
    const hour = new Date().getHours();
    if (hour < 11) return 'Selamat Pagi';
    if (hour < 15) return 'Selamat Siang';
    if (hour < 18) return 'Selamat Sore';
    return 'Selamat Malam';
  };

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const spiritualKit = settings.home_menu || [];
  const heroImage = settings.hero_image_url || 'https://images.pexels.com/photos/16619128/pexels-photo-16619128.jpeg';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[#F8F9FA]"
    >
      {/* Hero Section */}
      <div
        className="relative min-h-[45vh] bg-cover bg-center"
        style={{
          backgroundImage: `url('${heroImage}')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/90 to-emerald-800/95" />
        <div className="relative z-10 px-6 py-8 flex flex-col justify-between min-h-[45vh]">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              {settings.logo_url && (
                <motion.img
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  src={settings.logo_url}
                  alt="Logo"
                  className="w-12 h-12 rounded-full object-cover border-2 border-white/40 bg-white/10"
                  data-testid="home-logo"
                />
              )}
              <motion.h1
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl font-heading font-bold tracking-tight text-white"
                data-testid="home-mosque-name"
              >
                {settings.mosque_name}
              </motion.h1>
            </div>
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-white/80 text-sm font-body flex items-center gap-2"
            >
              <MapPin className="w-4 h-4" />
              {settings.location_city}
            </motion.p>
          </div>

          {/* Greeting & Prayer Countdown */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-white/90 text-lg font-body mb-4"
            >
              {getGreeting()} 🤲
            </motion.p>
            {nextPrayer && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="backdrop-blur-md bg-white/10 border border-white/20 rounded-3xl p-5"
                data-testid="prayer-countdown-widget"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-white" />
                    <span className="text-white text-sm font-medium">Menuju {nextPrayer.name}</span>
                  </div>
                  <span className="text-white/70 text-xs">{nextPrayer.time} WIB</span>
                </div>
                <div className="text-5xl font-body font-bold text-white tabular-nums tracking-tight">
                  {countdown}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Image Slider */}
      <ImageSlider images={settings.slider_images} />

      {/* Spiritual Kit Grid */}
      <div className="px-6 py-8">
        <h2 className="text-2xl font-heading font-semibold text-primary-dark mb-6">
          {settings.spirit_kit_title || 'Spiritual Kit'}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {spiritualKit.map((item, index) => {
            const Icon = getIcon(item.icon);
            return (
              <motion.button
                key={item.id}
                data-testid={`spiritual-${item.id}-btn`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                onClick={() => navigate(to(item.path))}
                className="bg-white rounded-3xl shadow-sm border border-stone-100 p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <span className="text-sm font-body font-medium text-text-primary text-center">
                  {item.label}
                </span>
              </motion.button>
            );
          })}
          {/* Pengurus Masjid - fixed menu item */}
          <motion.button
            data-testid="home-pengurus-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (spiritualKit.length) }}
            onClick={() => navigate(to('/pengurus'))}
            className="bg-white rounded-3xl shadow-sm border border-stone-100 p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-emerald-50">
              <Users className="w-6 h-6 text-emerald-700" />
            </div>
            <span className="text-sm font-body font-medium text-text-primary text-center">Pengurus</span>
          </motion.button>

          {/* Jadwal Imam & Khatib */}
          <motion.button
            data-testid="home-imam-khatib-btn"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * (spiritualKit.length + 1) }}
            onClick={() => navigate(to('/imam-khatib'))}
            className="bg-white rounded-3xl shadow-sm border border-stone-100 p-5 flex flex-col items-center gap-3 hover:shadow-md transition-shadow"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-amber-50">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <span className="text-sm font-body font-medium text-text-primary text-center">Jadwal Jumat</span>
          </motion.button>
        </div>
      </div>

      {/* Infaq Carousel */}
      {infaqPrograms.length > 0 && (
        <div className="pb-8">
          <div className="px-6 mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-heading font-semibold text-primary-dark">Program Donasi</h2>
            <button
              onClick={() => navigate(to('/infaq'))}
              className="text-sm font-body text-emerald-700 font-medium hover:underline"
              data-testid="home-infaq-all-btn"
            >
              Lihat Semua
            </button>
          </div>

          {/* Scrollable cards with peek */}
          <div
            ref={carouselRef}
            className="flex overflow-x-auto gap-3 pb-3 pl-6 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            data-testid="infaq-carousel"
          >
            {infaqPrograms.map((p, i) => {
              const isUmroh = p.program_type === 'umroh';
              const pctVal = Math.min((p.current_amount / (p.target_amount || 1)) * 100, 100);
              const remaining = Math.max((p.target_amount || 0) - (p.current_amount || 0), 0);
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                  onClick={() => navigate(to('/infaq'))}
                  className="flex-shrink-0 snap-start rounded-3xl overflow-hidden cursor-pointer bg-white shadow-md hover:shadow-lg transition-shadow border border-stone-200/70"
                  style={{ width: '82vw', maxWidth: 320 }}
                  data-testid={`home-infaq-card-${p.id}`}
                >
                  {/* Full-width image at top — NO text overlay */}
                  {p.image_url ? (
                    <div className="w-full h-32 sm:h-36 overflow-hidden">
                      <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                  ) : isUmroh ? (
                    <div className="w-full h-32 sm:h-36 bg-gradient-to-br from-[#3B2F0B] via-[#1E3A2F] to-[#022C22] flex items-center justify-center">
                      <Sparkles className="w-9 h-9 text-amber-300/40" />
                    </div>
                  ) : (
                    <div className="w-full h-32 sm:h-36 bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center">
                      <Heart className="w-9 h-9 text-emerald-200" />
                    </div>
                  )}

                  {/* White content area */}
                  <div className="p-3 sm:p-4">
                    {isUmroh ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold mb-2">
                        <Sparkles className="w-3 h-3" /> PREMIUM
                      </span>
                    ) : (
                      <span className="inline-block px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs mb-2">
                        {p.category || 'Infaq'}
                      </span>
                    )}

                    <h3 className="text-sm font-heading font-bold text-[#1C1917] mb-2.5 line-clamp-2">{p.title}</h3>

                    {/* Progress bar with % */}
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mb-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pctVal}%` }}
                        transition={{ duration: 1.2, ease: 'easeOut', delay: 0.1 * i }}
                        className={`h-full rounded-full ${isUmroh ? 'bg-amber-400' : 'bg-gradient-to-r from-amber-400 to-yellow-500'}`}
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-[#022C22]">{rp(p.current_amount)}</span>
                      <span className="text-xs text-stone-400">
                        {remaining > 0 ? `Kurang ${rp(remaining)}` : <span className="text-amber-600 font-semibold">{pctVal.toFixed(0)}%</span>}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            {/* Right spacing for peek visual */}
            <div className="flex-shrink-0 w-5" />
          </div>

          {/* Dot indicators */}
          {infaqPrograms.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-3" data-testid="infaq-carousel-dots">
              {infaqPrograms.map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ width: i === carouselIdx ? 20 : 6, opacity: i === carouselIdx ? 1 : 0.3 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 28 }}
                  className={`h-1.5 rounded-full ${i === carouselIdx ? 'bg-emerald-700' : 'bg-stone-400'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Warta & Kegiatan Slider */}
      {events.length > 0 && (
        <div className="pb-8">
          <div className="px-6 mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-heading font-semibold text-primary-dark">Warta & Kegiatan</h2>
            <button
              onClick={() => navigate(to('/warta'))}
              className="text-sm font-body text-emerald-700 font-medium hover:underline"
            >
              Lihat Semua
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 px-6 snap-x snap-mandatory scrollbar-hide">
            {events.map((event, i) => (
              <motion.button
                key={event.id}
                data-testid={`home-event-card-${event.id}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 * i }}
                onClick={() => setSelectedEvent(event)}
                className="flex-shrink-0 w-52 bg-white rounded-3xl shadow-sm border border-stone-100 overflow-hidden text-left snap-start hover:shadow-md transition-shadow"
              >
                {event.image_url ? (
                  <div
                    className="h-32 bg-cover bg-center"
                    style={{ backgroundImage: `url('${event.image_url}')` }}
                  />
                ) : (
                  <div className="h-32 bg-emerald-50 flex items-center justify-center">
                    <Calendar className="w-10 h-10 text-emerald-200" />
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-emerald-700 font-medium mb-1">{formatEventDate(event.date)}</p>
                  <h3 className="text-sm font-body font-semibold text-primary-dark line-clamp-2 leading-snug">
                    {event.title}
                  </h3>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={e => e.stopPropagation()}
              className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
              data-testid="event-detail-modal"
            >
              <div className="relative">
                {selectedEvent.image_url ? (
                  <img src={selectedEvent.image_url} alt={selectedEvent.title} className="w-full h-52 object-cover" />
                ) : (
                  <div className="w-full h-32 bg-emerald-50 flex items-center justify-center">
                    <Calendar className="w-12 h-12 text-emerald-200" />
                  </div>
                )}
                <button
                  onClick={() => setSelectedEvent(null)}
                  data-testid="event-modal-close-btn"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-body text-stone-500">{formatEventDate(selectedEvent.date)}</span>
                </div>
                <h2 className="text-xl font-heading font-bold text-primary-dark mb-3">{selectedEvent.title}</h2>
                <p className="text-sm font-body text-stone-700 leading-relaxed whitespace-pre-wrap">
                  {selectedEvent.description}
                </p>
                {selectedEvent.video_url && (
                  <a
                    href={selectedEvent.video_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-sm font-body font-medium text-emerald-700"
                  >
                    <Video className="w-4 h-4" /> Tonton Video
                  </a>
                )}
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="w-full mt-5 py-3 bg-stone-100 text-stone-700 rounded-full font-body font-medium text-sm"
                >
                  Tutup
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Prayer Times */}
      {prayerTimes && (
        <div className="px-6 pb-8">
          <h3 className="text-lg font-body font-medium text-text-primary mb-4">
            Jadwal Sholat Hari Ini
          </h3>
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-5 space-y-3">
            {[
              { name: 'Subuh', time: prayerTimes.Fajr },
              { name: 'Dzuhur', time: prayerTimes.Dhuhr },
              { name: 'Ashar', time: prayerTimes.Asr },
              { name: 'Maghrib', time: prayerTimes.Maghrib },
              { name: 'Isya', time: prayerTimes.Isha }
            ].map((prayer) => (
              <div key={prayer.name} className="flex justify-between items-center">
                <span className="text-sm font-body text-text-secondary">{prayer.name}</span>
                <span className="text-sm font-body font-semibold text-emerald-800 tabular-nums">
                  {prayer.time}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Absen Berjamaah GPS */}
      <div className="px-6 pb-8">
        <motion.button
          data-testid="home-absen-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate(to('/absen'))}
          whileTap={{ scale: 0.98 }}
          className="w-full flex items-center gap-4 rounded-3xl p-5 text-left text-white shadow-sm"
          style={{ background: 'linear-gradient(135deg, #065F46 0%, #022C22 100%)' }}
        >
          <div className="w-12 h-12 rounded-2xl bg-amber-400/90 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-6 h-6 text-emerald-950" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-amber-300">Absen Sholat Berjamaah</h3>
            <p className="text-xs font-body text-white/70">Catat kehadiran Anda saat berada di masjid (radius 50m)</p>
          </div>
        </motion.button>
      </div>

      {/* Financial Widget */}
      <FinancialWidget />

      {/* Download App Widget */}
      <DownloadAppWidget />

      {/* Bottom padding for nav */}
      <div className="pb-24" />
    </motion.div>
  );
};

export default HomePage;